/**
 * FR-017 #4c — a twin item's quantity links, totalled per unit.
 *
 * The object page's "Mengen aus Belegen" reads the item's own link rows (the
 * same `GET /twin/links?target_type&target_id` Belege reads) and sums them
 * here. Two rules keep the totals honest:
 *
 * - Units are compared trimmed and case-insensitively, and never converted:
 *   `kg` and `t` are two groups, because a conversion table would be a guess.
 * - Sums are exact decimals (scaled integers), so `0,1 + 0,2` is `0,3`. A link
 *   whose quantity or direction cannot be read is returned in `unreadable` —
 *   counted, never summed as zero.
 *
 * Only a link whose document and target are both still there counts
 * (`link_state` "linked", `target_state` "live" — absent on an older response).
 * A payload quantity is read with `payloadDecimal`, never `parseLineNumber`:
 * that one reads a document's printed row and strips letters, so the `"1e3"`
 * the link validator accepts would total as 13 (codex r1 over #4c).
 */
import type { DocumentLink } from '../../services/twinService';
import { isoDate } from './lineRows';

export type QuantityDirection = 'in' | 'out';

export interface QuantityRow {
  link: DocumentLink;
  /** Canonical decimal (`1240.5`). */
  quantity: string;
  direction: QuantityDirection;
  /** `yyyy-mm-dd`, or `null` when the link carries no readable date. */
  observedAt: string | null;
}

export interface QuantityGroup {
  /** The first spelling of this unit, trimmed; `''` for a link without one. */
  unit: string;
  inTotal: string;
  outTotal: string;
  /** Zugang − Abgang. */
  balance: string;
  rows: QuantityRow[];
}

export interface QuantityTotals {
  groups: QuantityGroup[];
  unreadable: DocumentLink[];
}

/** A canonical decimal as an integer scaled by 10^scale. */
function scaled(canonical: string, scale: number): bigint {
  const negative = canonical.startsWith('-');
  const [int, frac = ''] = (negative ? canonical.slice(1) : canonical).split('.');
  const digits = BigInt(`${int}${frac.padEnd(scale, '0')}`);
  return negative ? -digits : digits;
}

function fractionDigits(canonical: string): number {
  const dot = canonical.indexOf('.');
  return dot < 0 ? 0 : canonical.length - dot - 1;
}

/** An integer scaled by 10^scale back to a canonical decimal (no trailing zeros). */
function unscaled(value: bigint, scale: number): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(scale + 1, '0');
  const int = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale).replace(/0+$/, '');
  const text = frac ? `${int}.${frac}` : int;
  return negative && text !== '0' ? `-${text}` : text;
}

function sum(values: string[], scale: number): bigint {
  return values.reduce((acc, v) => acc + scaled(v, scale), 0n);
}

const FLOAT_GRAMMAR = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

/**
 * A link payload number as a canonical decimal (`"1e3"` → `"1000"`), else `null`.
 *
 * The grammar is what the link validator accepts (anything Python's `float()`
 * reads, minus the non-finite), expanded exactly rather than through a float.
 * The frontend mirror of `hofsync_twin_links.payload_decimal`: a German comma
 * is not a payload number (the form sends `"22.5"`, never `"22,50"`).
 */
export function payloadDecimal(raw: unknown): string | null {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    raw = String(raw);
  }
  if (typeof raw !== 'string') return null;
  const m = FLOAT_GRAMMAR.exec(raw.trim());
  if (!m) return null;
  const [, sign, int = '', frac = '', exp = '0'] = m;
  if (int === '' && frac === '') return null;
  // Shift the decimal point by the exponent over the digit string.
  const digits = `${int}${frac}`;
  const point = int.length + Number(exp);
  const padded = point < 0 ? '0'.repeat(-point) + digits : digits.padEnd(point, '0');
  const at = Math.max(point, 0);
  const whole = padded.slice(0, at).replace(/^0+(?=\d)/, '') || '0';
  const fraction = padded.slice(at).replace(/0+$/, '');
  const text = fraction ? `${whole}.${fraction}` : whole;
  return sign === '-' && /[1-9]/.test(text) ? `-${text}` : text;
}

function counts(link: DocumentLink): boolean {
  if (link.link_state !== undefined && link.link_state !== 'linked') return false;
  return link.target_state === undefined || link.target_state === 'live';
}

function readRow(link: DocumentLink): QuantityRow | null {
  const p = link.payload ?? {};
  const quantity = payloadDecimal(p.quantity);
  const direction = p.direction === 'in' || p.direction === 'out' ? p.direction : null;
  if (quantity === null || direction === null) return null;
  const observedAt = typeof p.observed_at === 'string' ? isoDate(p.observed_at) : null;
  return { link, quantity, direction, observedAt };
}

/**
 * Newest first; a same-date tie by the label the row shows (codex r1 over #4c),
 * then by document and link id so the order never depends on the response's.
 */
function newestFirst(labelOf: (link: DocumentLink) => string) {
  return (a: QuantityRow, b: QuantityRow): number =>
    (b.observedAt ?? '').localeCompare(a.observedAt ?? '') ||
    labelOf(a.link).localeCompare(labelOf(b.link), 'de-DE') ||
    a.link.document_id.localeCompare(b.link.document_id) ||
    a.link.link_id.localeCompare(b.link.link_id);
}

/**
 * `labelOf` is the document label the panel shows beside each row — the
 * same-date tie-break, so rows read in the order their labels do.
 */
export function quantityTotals(
  links: readonly DocumentLink[],
  labelOf: (link: DocumentLink) => string = (link) => link.document_id,
): QuantityTotals {
  const byUnit = new Map<string, { unit: string; rows: QuantityRow[] }>();
  const unreadable: DocumentLink[] = [];

  for (const link of links) {
    if (link.link_type !== 'quantity' || !counts(link)) continue;
    const row = readRow(link);
    if (row === null) {
      unreadable.push(link);
      continue;
    }
    const raw = link.payload?.unit;
    const unit = typeof raw === 'string' ? raw.trim() : '';
    const key = unit.toLocaleLowerCase('de-DE');
    const group = byUnit.get(key);
    if (group) group.rows.push(row);
    else byUnit.set(key, { unit, rows: [row] });
  }

  const groups = [...byUnit.values()].map(({ unit, rows }) => {
    const scale = Math.max(0, ...rows.map((r) => fractionDigits(r.quantity)));
    const inTotal = sum(
      rows.filter((r) => r.direction === 'in').map((r) => r.quantity),
      scale,
    );
    const outTotal = sum(
      rows.filter((r) => r.direction === 'out').map((r) => r.quantity),
      scale,
    );
    return {
      unit,
      inTotal: unscaled(inTotal, scale),
      outTotal: unscaled(outTotal, scale),
      balance: unscaled(inTotal - outTotal, scale),
      rows: [...rows].sort(newestFirst(labelOf)),
    };
  });

  return { groups, unreadable };
}
